import { ChangeEvent, useMemo } from 'react';
import { Stack, Flex, Switch, Box } from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { storeSelector, usePolicyContext } from '@Policy/store';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import { FormLabel } from '@Components/Common/FormComponent';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { useRiskAIRules } from '../hooks/useRiskAIRules';
import { RuleSelection } from '../components/RuleSelection';
import { useRiskAIRulesQuery } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/query';
import { RuleType } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { rulesListLinks } from '../shared';
import { useSocialGraphEnabledQuery } from 'src/pages/GlobalSetting/SocialGraph/query';
import { filterSocialGraphRules } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/utils';

function CustomCorrelatedIntelligenceTab() {
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  const contextSelector = storeSelector(contextStore);

  const {
    customizedRuleNotifyType,
    tmRaiFilterRules,
    policyType,
    setRiskAiState,
  } = contextSelector((s) => {
    const template = s.template as ExchangeOnlinePolicyItem;
    return {
      customizedRuleNotifyType: template.tmRiskAiFilters.raiCustomNotifyType,
      tmRaiFilterRules: template.tmRiskAiFilters.tmRaiFilterRules,
      policyType: template.policyType,
      setRiskAiState: s.setRiskAiState,
    };
  });

  const { data: _rules } = useRiskAIRulesQuery({
    hideSocialGraph: [
      PolicyTypeEnum.Gmail,
      PolicyTypeEnum.ExchangeInline,
      PolicyTypeEnum.GmailInline,
    ].includes(policyType),
  });
  const { data: socialGraphEnabled } = useSocialGraphEnabledQuery();

  const rules = useMemo(() => {
    if (!_rules) return undefined;
    return filterSocialGraphRules(
      _rules ?? [],
      socialGraphEnabled ?? false,
      policyType
    );
  }, [_rules, policyType, socialGraphEnabled]);

  const { customRules, selectedCustomRules, setSelectedCustomRules } =
    useRiskAIRules({
      rules: rules || [],
      policyRules: tmRaiFilterRules,
      onRulesUpdate: (rules) => setRiskAiState({ tmRaiFilterRules: rules }),
    });

  return (
    <Stack gap="6x" p="4x">
      <Stack gap="4x">
        <Box>
          <RuleSelection
            allRules={customRules}
            selectedRules={selectedCustomRules}
            description={t(
              'policy.riskAi.anomalies.availableRules.userDefined.desc'
            )}
            onSelectionChange={setSelectedCustomRules}
            rulesListLink={rulesListLinks[RuleType.UserDefined]}
          />
        </Box>
        <Flex alignItems="center">
          <FormLabel label={t('policy.label.notification')} />
          <Switch
            value={customizedRuleNotifyType}
            checked={!!customizedRuleNotifyType}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setRiskAiState({
                raiCustomNotifyType: Number(e.target.checked),
              });
            }}
          >
            {customizedRuleNotifyType === 0
              ? t('policy.status.off')
              : t('policy.status.on')}
          </Switch>
        </Flex>
      </Stack>
    </Stack>
  );
}

export default CustomCorrelatedIntelligenceTab;
